import React, { useEffect, useState } from 'react';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { t } from '../../../types/translation/Translator';
import { BaseFilter } from '../../../VentoryUI/components/filters/common/BaseFilter';
import { FilterProps } from '../../../VentoryUI/components/filters/common/filter.util';
import Icon, { IconType } from '../../icons/Icon';
import { Button, ButtonVariant } from '../Button';
import FilterModal from './FilterModal';

interface FilterElementProps<T> {
  filter: BaseFilter<T>;
  items: T[];
  setItems: (items: T[]) => void;
  query?: string;
  setQuery?: (query: string) => void;
  loading?: boolean;
  onChange?: () => void;
}

export default function Filter<T>({
  filter,
  items,
  setItems,
  query = '',
  setQuery,
  loading = false,
  onChange,
}: FilterElementProps<T>) {
  const [open, setOpen] = useState(false);
  const [filterProps, _setFilterProps] = useState<FilterProps>(filter.getFilterProps());

  const setFilterProps = (filterProps: FilterProps) => {
    _setFilterProps(filterProps);
    filter.saveFilterProps(filterProps);
  };

  const handleFilter = () => {
    if (!items.length) return setItems([]);
    setItems([...filter.filteredItems(items, query, filterProps)]);
  };

  useEffect(() => {
    filter.saveFilterProps({ ...filterProps, query: query });
    handleFilter();
  }, [query]);

  useEffect(() => {
    handleFilter();
  }, [loading, filterProps]);

  useEffect(() => {
    handleFilter();
  }, [items]);

  useEffect(() => {
    if (onChange) onChange();
  }, [filterProps]);

  useEffect(() => {
    if (setQuery) setQuery(filterProps.query || '');
  }, []);

  if (!filter.filterOptions().length) return <></>;

  const filterCount = filter.filterOptionsCount({ filterProps, setFilterProps });

  return (
    <>
      <FilterModal
        open={open}
        setOpen={setOpen}
        onClose={() => setOpen(false)}
        filter={filter}
        filterProps={filterProps}
        setFilterProps={setFilterProps}
      />

      <Button
        leading={<Icon type={IconType.filter} size={'size-5'} />}
        trailing={
          filterCount ? (
            <div className='flex size-5 items-center justify-center rounded-full bg-primary text-xs text-on-primary'>
              {filterCount}
            </div>
          ) : null
        }
        text={t(TranslationKey.filter).singular.label}
        onClick={() => setOpen(true)}
        variant={ButtonVariant.filter}
      />
    </>
  );
}
